import { useQuery } from "@apollo/client";

import type { OnboardingDerivedStateQuery } from "@medbillai/generated-graphql-types";

import { errorHandling } from "../../../lib/apollo/utils";
import { useAuth } from "../../../lib/auth";
import type { OnboardingStateData } from "../deriveInitial";
import type { OnboardingIdentity } from "../types";
import { onboardingStateQuery } from "./queries";

// Canonical UserMemory keys the FA prescreen writes (see checkFaEligibility).
const HOUSEHOLD_SIZE_MEMORY_KEY = "user.profile.householdSize";
const HOUSEHOLD_INCOME_MEMORY_KEY = "user.profile.householdIncome";
// Written by recordInsuranceStatus — the Insurance step's "I'm uninsured"
// answer (and the case checklist's equivalent).
const INSURANCE_STATUS_MEMORY_KEY = "user.profile.insuranceStatus";

const safeJsonParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * `projection__UserMemory.entries` is a JSON scalar map keyed by memoryType.
 * Codegen types it as `string` but the wire delivers the parsed object, so
 * accept both and treat anything else as "no entries".
 */
const memoryEntriesOf = (rawEntries: unknown): object | null => {
  const entries =
    typeof rawEntries === "string" ? safeJsonParse(rawEntries) : rawEntries;
  if (
    typeof entries !== "object" ||
    entries === null ||
    Array.isArray(entries)
  ) {
    return null;
  }
  return entries;
};

const containsFaAnswers = (rawEntries: unknown): boolean => {
  const entries = memoryEntriesOf(rawEntries);
  return (
    entries !== null &&
    HOUSEHOLD_SIZE_MEMORY_KEY in entries &&
    HOUSEHOLD_INCOME_MEMORY_KEY in entries
  );
};

const containsInsuranceStatus = (rawEntries: unknown): boolean => {
  const entries = memoryEntriesOf(rawEntries);
  return entries !== null && INSURANCE_STATUS_MEMORY_KEY in entries;
};

/**
 * All three or nothing — a partial Member row enrolls the user and is then
 * skipped on every eligibility check.
 */
const identityOf = (
  persons: NonNullable<
    NonNullable<OnboardingDerivedStateQuery["me"]>["persons"]
  >,
): OnboardingIdentity | null => {
  const person = persons.find(p => p.primary);
  const firstName = (person?.givenNames ?? []).join(" ").trim();
  const lastName = person?.familyName?.trim() ?? "";
  const dateOfBirth = person?.dateOfBirth ?? "";
  return firstName !== "" && lastName !== "" && dateOfBirth !== ""
    ? { firstName, lastName, dateOfBirth }
    : null;
};

/**
 * Reads the presence flags that drive resume derivation. The query is skipped
 * until the auth session has hydrated; a failed read degrades to starting
 * over rather than blocking entry.
 */
export const useOnboardingState = (): {
  loading: boolean;
  state: OnboardingStateData | null;
} => {
  const { isAuthenticated, isHydrated } = useAuth();

  const resolvingCaller = !isHydrated;
  const resolvable = isAuthenticated;

  const { data, loading, error } = useQuery(onboardingStateQuery, {
    skip: resolvingCaller || !resolvable,
    // Resume must reflect canonical truth, not a cached snapshot from
    // earlier in the session.
    fetchPolicy: "network-only",
    context: errorHandling("caller"),
  });

  if (resolvingCaller || loading) {
    return { loading: true, state: null };
  }
  if (!resolvable || error !== undefined || data === undefined) {
    return { loading: false, state: null };
  }
  return {
    loading: false,
    state: {
      // The primary (self) person only — a dependent-only account (e.g. from
      // a bill flow) must still pass through Complete profile.
      hasIdentity: (data.me?.persons ?? []).some(person => person.primary),
      identity: identityOf(data.me?.persons ?? []),
      // Answered, not insured: segmentation's `hasInsurance` only flips on a
      // carrier selection, so an uninsured declaration (recorded in user
      // memory) must also satisfy the resume gate or reload reopens the step.
      hasInsuranceAnswer:
        (data.me?.segmentationData?.hasInsurance ?? false) ||
        (data.projection__UserMemory ?? []).some(memory =>
          containsInsuranceStatus(memory.entries),
        ),
      features: data.me?.segmentationData?.features ?? null,
      hasFaAnswers: (data.projection__UserMemory ?? []).some(memory =>
        containsFaAnswers(memory.entries),
      ),
      partner: data.viewerPartner ?? null,
    },
  };
};
